import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { projectRouter } from "@editor/trpc/project";

import { skipToken } from "@tanstack/react-query";
import {
  BENCHMARKS_TIMEFRAME,
  createCompositeKey,
  getAovBracket,
} from "analytics-utils";

import {
  BENCHMARKS,
  calculateOverallBenchmarks,
  METRICS_REQUIRING_PRORATION,
} from "./benchmarks";
import { getChartIntervalOptions } from "./chart";
import { useAnalyticsQueryContext } from "./contexts/AnalyticsQueryContext";

export const useBenchmarks = ({ aov }: { aov: number }) => {
  const projectId = useCurrentProjectId();
  const { query, timeZone } = useAnalyticsQueryContext();

  const { data: fetchedIndustry } =
    projectRouter.getIndustryForBenchmarks.useQuery(
      projectId ? { projectId } : skipToken,
    );

  const industry = fetchedIndustry ?? "all Replo stores";

  const aovBracket = getAovBracket(aov);

  const compositeKey = createCompositeKey(industry, aovBracket);

  const benchmarksTuple = BENCHMARKS.find(([key]) => key === compositeKey);

  /**
   * TODO (Max, 2025-06-24): If there's no benchmarks for that given industry & AOV bracket, we
   * simply use the overall Replo benchmarks. An improvement would be to first look at the benchmarks
   * for the industry without caring about AOV bracket, and only if that also leads to no results
   * should we use the overall benchmarks.
   */
  const benchmarks =
    benchmarksTuple?.[1] ?? calculateOverallBenchmarks(aovBracket);

  const { rangeInDays: queryRangeInDays } = getChartIntervalOptions(
    query.selectedTimePeriod,
    timeZone,
  );

  const { rangeInDays: benchmarkRangeInDays } = getChartIntervalOptions(
    BENCHMARKS_TIMEFRAME,
    timeZone,
  );

  const prorateBenchmarks = (value: number) => {
    return (value / benchmarkRangeInDays) * queryRangeInDays;
  };

  /**
   * NOTE (Max, 2025-06-25): We prorate the benchmarks to match the query range:
   * this is necessary as the benchmarks are computed over a 30 day period, while the
   * query range can be shorter. It wouldn't make sense to calculate e.g. revenue
   * from benchmarks (over 30 days) with the user's store's revenue for the query's range
   * (e.g. 7 days).
   *
   * However, for metrics that don't require proration (i.e. metrics that are ratio-based, like CVR/AOV),
   * we don't need that.
   */
  const proratedBenchmarks = (() => {
    const result = { ...benchmarks };

    METRICS_REQUIRING_PRORATION.forEach((metric) => {
      if (metric in result) {
        result[metric] = prorateBenchmarks(result[metric]);
      }
    });

    // NOTE (Max, 2025-06-25): The backend returns the conversion rate as a decimal,
    // so we need to multiply by 100 to get the percentage.
    result.conversion_rates = benchmarks.conversion_rates * 100;

    return result;
  })();

  return {
    benchmarks: proratedBenchmarks,
    industry,
    aovBracket,
    benchmarkRangeInDays,
  };
};
